import { EditOutlined } from "@mui/icons-material";
import {
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  Pagination,
  TextField,
} from "@mui/material";
import colors from "assets/theme/base/colors";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import TLinkApi from "services/tlink.api";

const AllowBuyers = ({
  buyerList,
  setIsLoading,
  selectedClient,
  setSelectedClient,
  page,
  setPage,
  totalLength,
  entriesPerPage,
  // getBuyerList,
}) => {
  const { grey } = colors;
  const { id } = useParams();
  const [addData, setAddData] = useState(true);
  const [isEdit, setIsEdit] = useState({
    disabled: true,
    edit: false,
  });
  const [blockList, setBlockList] = useState([]);
  const [search, setSearch] = useState("");
  let { enqueueSnackbar } = useSnackbar();

  const GetBlockBuyers = async () => {
    setIsLoading(true);
    try {
      let data = await TLinkApi.get(
        `vendors/${id}/client/${selectedClient}/buyers?page=${1}&limit=${50000}`
      );
      if (data?.data?.buyers && data?.data.buyers.length > 0) {
        setBlockList(data?.data?.buyers.map((val) => String(val.buyer_id)));
        setAddData(false);
        setIsEdit({
          disabled: true,
          edit: false,
        });
      } else {
        setAddData(true);
        setIsEdit({
          disabled: false,
          edit: false,
        });
      }
      setIsLoading(false);
    } catch (e) {
      console.log(e);
      enqueueSnackbar(e?.response?.data.message, {
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClient) {
      GetBlockBuyers();
    }
  }, [selectedClient]);

  const save = async () => {
    setIsLoading(true);
    try {
      let payload = {
        buyerIds: blockList,
      };
      let result = await TLinkApi.put(
        `/vendors/${id}/client/${selectedClient}/buyers`,
        payload
      );
      enqueueSnackbar(result?.message, {
        variant: "success",
      });

      setIsEdit({
        disabled: true,
        edit: false,
      });
      await GetBlockBuyers();
      setIsLoading(false);
    } catch (e) {
      console.log(e);
      setIsLoading(false);
      enqueueSnackbar(e?.message, {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    setBlockList([]);
    setPage(1);
  }, [selectedClient]);

  const startIndex = (page - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedBlocks = useMemo(() => {
    const filtered = search
      ? buyerList.filter((item) =>
          item.name?.toLowerCase().includes(search.toLowerCase())
        )
      : buyerList;

    return (
      filtered
        .sort((a, b) => {
          if (isEdit.edit || addData) {
            // Simple alphabetical (case-insensitive) comparison without localeCompare
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();

            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return a.name.localeCompare(b.name);
          } else {
            // clientConfig inclusion priority sorting
            const aIncluded = blockList.includes(String(a.id)) ? -1 : 1;
            const bIncluded = blockList.includes(String(b.id)) ? -1 : 1;
            if (aIncluded !== bIncluded) {
              return aIncluded - bIncluded;
            }
            return a.name.localeCompare(b.name);
          }
        })
        .slice(startIndex, endIndex) || []
    );
  }, [startIndex, buyerList, endIndex, search, isEdit, addData]);

  return (
    <MDBox
      py={2}
      sx={{
        background: "white",
        borderRadius: "1rem",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        pb="0.5rem"
        px={2}
        sx={{ borderBottom: `1px solid ${grey[400]}` }}
      >
        <Typography variant="v6" fontWeight="600">
          Client Buyers
        </Typography>

        {isEdit.disabled ? (
          <MDButton
            size="small"
            variant="contained"
            color="secondary"
            sx={{ display: !selectedClient ? "none" : "" }}
            startIcon={<EditOutlined />}
            onClick={() => {
              setIsEdit({
                disabled: false,
                edit: true,
              });
            }}
          >
            Edit
          </MDButton>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ display: !selectedClient ? "none" : "" }}
          >
            <MDButton
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => {
                if (addData) {
                  setBlockList([]);
                  setSelectedClient(null);
                } else {
                  GetBlockBuyers();
                }
              }}
            >
              &nbsp;cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              // disabled={blockList.length <= 0}
              size="small"
              onClick={() => {
                save();
              }}
            >
              Save
            </MDButton>
          </Stack>
        )}
      </Stack>
      <Stack
        sx={{
          height:
            !buyerList?.length > 0
              ? "calc(100vh - 275px)"
              : selectedClient
              ? "calc(100vh - 317px)"
              : "calc(100vh - 275px)",
          overflowY: "auto",
        }}
      >
        {selectedClient ? (
          <MDBox px={1} sx={{ position: "relative" }}>
            <Stack
              py={1}
              sx={{
                position: "sticky",
                top: "0",
                background: "white",
                zIndex: "2",
              }}
            >
              <TextField
                placeholder="Search here"
                value={search}
                fullWidth
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            </Stack>
            {isEdit.edit || addData ? (
              <MDBox
                sx={{
                  padding: isEdit.disabled ? "0.45rem 0.5rem" : "0.5rem",
                  borderBottom: `1px solid ${grey[400]}`,
                  display: buyerList?.length === 0 ? "none" : "",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={blockList.length === buyerList.length}
                      color="secondary"
                      onClick={(e) => {
                        if (e.target.checked) {
                          setBlockList(buyerList.map((val) => String(val.id)));
                        } else {
                          setBlockList([]);
                        }
                      }}
                    />
                  }
                  label={"Select All"}
                />
              </MDBox>
            ) : (
              ""
            )}
            {isEdit.edit || addData ? (
              paginatedBlocks?.length > 0 ? (
                paginatedBlocks.map((buyer, i) => (
                  <MDBox
                    sx={{
                      padding: isEdit.disabled ? "0.45rem 0.5rem" : "0.5rem",
                      borderBottom: `1px solid ${grey[400]}`,
                    }}
                    key={i}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={blockList.includes(String(buyer?.id))}
                          color="secondary"
                          onChange={(e) => {
                            console.log(buyer?.id, "buyer?.id");
                            if (blockList.includes(String(buyer?.id))) {
                              let payload = [...blockList];
                              payload = payload.filter(
                                (val) => val !== String(buyer?.id)
                              );
                              setBlockList(payload);
                            } else {
                              let payload = [...blockList];
                              payload.push(String(buyer?.id));
                              setBlockList(payload);
                            }
                          }}
                        />
                      }
                      label={buyer.name}
                    />
                  </MDBox>
                ))
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  mt={2}
                >
                  <Typography
                    fontSize="15px"
                    color="dark"
                    fontWeight="500"
                    textAlign="center"
                  >
                    No buyers here !
                  </Typography>
                </Stack>
              )
            ) : paginatedBlocks?.length > 0 ? (
              paginatedBlocks.map((buyer, i) => (
                <MDBox
                  sx={{
                    padding: isEdit.disabled ? "0.45rem 0.5rem" : "0.5rem",
                    borderBottom: `1px solid ${grey[400]}`,
                  }}
                  key={i}
                >
                  <FormControlLabel
                    disabled
                    sx={{ opacity: "0.7" }}
                    control={
                      <Checkbox
                        checked={blockList.includes(String(buyer?.id))}
                        color="secondary"
                      />
                    }
                    label={buyer?.name}
                  />
                </MDBox>
              ))
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                mt={2}
              >
                <Typography
                  fontSize="15px"
                  color="dark"
                  fontWeight="500"
                  textAlign="center"
                >
                  No buyers here !
                </Typography>
              </Stack>
            )}
          </MDBox>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            mt={2}
          >
            <Typography
              fontSize="15px"
              color="dark"
              fontWeight="500"
              textAlign="center"
            >
              Please select client to see its buyers
            </Typography>
          </Stack>
        )}
      </Stack>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        mt={1}
        sx={{
          display: !buyerList?.length > 0 || !selectedClient ? "none" : "",
        }}
      >
        <Pagination
          count={Math.ceil(totalLength / entriesPerPage)}
          page={page}
          onChange={(e, newPage) => setPage(newPage)}
          color="primary"
          size="medium"
          showFirstButton
          showLastButton
          siblingCount={0}
          boundaryCount={1}
        />
      </Stack>
    </MDBox>
  );
};

export default AllowBuyers;
